import { BUSINESS, IMAGES } from "@/constants/business";
import { useLang } from "@/context/LangContext";
import { STRINGS } from "@/i18n";

export default function Hero() {
  const { lang } = useLang();
  const t = STRINGS[lang];
  return (
    <header className="relative px-6 pt-16 pb-24 md:pt-24 md:pb-32 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <p className="mono-lbl text-xs tracking-[0.2em] uppercase text-[#a4133c] mb-4 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-[#a4133c] animate-pulse" />
          {t.hero.badge}
        </p>
        <h1 className="serif-disp text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight font-light mb-6">
          <span className="ln"><span>{t.hero.h1a}</span></span>
          <span className="ln"><span>{t.hero.h1b}</span></span>
          <span className="ln"><span className="italic text-[#c9933b]">{t.hero.h1c}</span></span>
        </h1>
        <p className="d1-fade text-neutral-300 text-lg md:text-xl font-light max-w-xl leading-relaxed mb-4">
          {t.hero.sub}
        </p>
        <p className="d1-fade mono-lbl text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-8">
          {t.hero.tagline}
        </p>
        <div className="d1-fade flex flex-wrap items-center gap-4">
          <a
            data-testid="hero-book-btn"
            href="#booking"
            className="w-full sm:w-auto text-center px-6 py-3.5 bg-gradient-to-r from-[#800f2f] to-[#a4133c] text-white text-xs uppercase tracking-widest font-semibold rounded hover:shadow-[0_0_25px_rgba(164,19,60,0.5)] transition-all"
          >
            {t.hero.book}
          </a>
          <a
            data-testid="hero-whatsapp-btn"
            href={BUSINESS.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto text-center px-6 py-3.5 border border-white/20 hover:border-white/50 text-neutral-200 text-xs uppercase tracking-widest rounded transition-all"
          >
            {t.hero.whatsapp}
          </a>
        </div>
      </div>

      <div className="lg:col-span-5 relative">
        <div className="d1-wipe relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
          <img
            data-testid="hero-image"
            src={IMAGES.hero}
            alt="Mahogany — long braids, velvet touch, mocha warmth"
            className="absolute inset-0 w-full h-full object-cover object-[center_15%]"
          />
          <div className="absolute top-3 left-3 w-20 md:w-24 aspect-[4/5] rounded-lg overflow-hidden border border-[#c9933b]/40 shadow-xl -rotate-6">
            <img
              data-testid="hero-mood-card"
              src={IMAGES.oilPour}
              alt="Warm oil poured by candlelight"
              className="w-full h-full object-cover"
            />
          </div>
          <div
            className="d1-drift absolute inset-0"
            style={{
              background:
                "radial-gradient(120% 90% at 30% 20%, #ff758f1f 0%, transparent 55%), linear-gradient(160deg, transparent 50%, #0d0906dd)",
            }}
          />
          <div className="absolute inset-0 opacity-15 pointer-events-none">
            <svg className="w-full h-full" aria-hidden="true">
              <filter id="d1g"><feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" /></filter>
              <rect width="100%" height="100%" filter="url(#d1g)" />
            </svg>
          </div>
          <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-black/60 backdrop-blur-md border border-white/10">
            <div className="flex items-center justify-between text-xs">
              <span className="mono-lbl text-[#ffb3c1] uppercase tracking-wider">{t.hero.capL}</span>
              <span className="text-neutral-400">{t.hero.capR}</span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
